import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import { useAppContext } from "../../../../contexts/AppContext";
import { findAirlineLogoFromAdmin } from "../../../../utils/airlineLogoFromAdmin";

const kiwiAirlineLogoUrl = (code, px = 64) =>
  `https://images.kiwi.com/airlines/${px}/${encodeURIComponent(String(code || "").trim())}.png`;

const airhexAirlineLogoUrl = (code) => {
  const c = String(code || "")
    .trim()
    .toUpperCase();
  if (!c) return null;
  return `https://content.airhex.com/content/logos/airlines_${c}_350_100_r.png`;
};

function hhmmForLeg(leg) {
  const tryRaw = (raw) => {
    if (raw == null || String(raw).trim() === "") return null;
    const d = dayjs(String(raw).trim());
    return d.isValid() ? d.format("HH:mm") : null;
  };
  const dep =
    leg.departureDateTime != null && String(leg.departureDateTime).trim() !== ""
      ? tryRaw(leg.departureDateTime)
      : null;
  const arr =
    leg.arrivalDateTime != null && String(leg.arrivalDateTime).trim() !== ""
      ? tryRaw(leg.arrivalDateTime)
      : null;
  const depShown = dep ?? (leg.depClock && leg.depClock !== "" ? leg.depClock : null) ?? "—";
  const arrShown = arr ?? (leg.arrClock && leg.arrClock !== "" ? leg.arrClock : null) ?? "—";
  return { depShown, arrShown };
}

function SegmentAirlineLogo({ adminLogoUrl, airlineCode }) {
  const code = String(airlineCode || "")
    .trim()
    .toUpperCase();
  const airhex = code ? airhexAirlineLogoUrl(code) : null;
  const kiwi = code ? kiwiAirlineLogoUrl(code, 64) : null;
  const candidates = useMemo(
    () => [adminLogoUrl, airhex, kiwi].filter(Boolean),
    [adminLogoUrl, airhex, kiwi]
  );
  const [failCount, setFailCount] = useState(0);

  if (candidates.length === 0 || failCount >= candidates.length) {
    return (
      <div
        className="hr-fp-flight__logo hr-fp-flight__logo--row hr-fp-flight__logo--ph"
        aria-hidden
      />
    );
  }

  return (
    <img
      src={candidates[failCount]}
      alt=""
      className="hr-fp-flight__logo hr-fp-flight__logo--row"
      loading="lazy"
      onError={() => setFailCount((n) => n + 1)}
    />
  );
}

/**
 * בלוק כיוון טיסה בתוך כרטיס טיסה משולב (embedded) או לבד.
 */
export default function FlightCard({ segmentMeta, legTimelines, embedded = false }) {
  const { airlines } = useAppContext();

  if (!legTimelines || legTimelines.length === 0) {
    return null;
  }

  const labelHe = segmentMeta?.labelHe || "טיסה";
  const isDirect =
    segmentMeta != null ? segmentMeta.isDirect : (legTimelines?.length || 0) <= 1;
  const stopOrType = isDirect ? "ישירה" : segmentMeta?.stopLabelHe || "עצירה / קונקשן";

  const rootClass = "hr-fp-flight" + (embedded ? " hr-fp-flight--embedded" : "");

  const metaStripParts = [stopOrType, segmentMeta?.duration ? `משך ${segmentMeta.duration}` : null].filter(
    Boolean
  );

  return (
    <div
      className={rootClass}
      dir="rtl"
      data-fp-component="FlightCard"
      data-fp-legs={String(legTimelines?.length ?? 0)}
    >
      <header className="hr-fp-flight__head">
        <span className="hr-fp-flight__dir-title">{labelHe}</span>
      </header>

      {segmentMeta?.depDateDisplay ? (
        <div className="hr-fp-flight__date" translate="no">
          תאריך: {segmentMeta.depDateDisplay}
        </div>
      ) : null}

      {(legTimelines || []).map((leg, idx) => {
        const { depShown, arrShown } = hhmmForLeg(leg);
        const legCode = leg.airlineCode || segmentMeta?.airlineCode || "";
        const adminForLeg = findAirlineLogoFromAdmin(airlines, legCode);
        const flightNumLine = [legCode, leg.flightNumber].filter(Boolean).join(" ").trim();

        return (
          <div key={leg.key} className="hr-fp-flight__timeline-block">
            {(legTimelines || []).length > 1 ? (
              <div className="hr-fp-flight__leg-label">מקטע {idx + 1}</div>
            ) : null}

            <div className="flight-row hr-fp-flight__flight-row">
              <div className="hr-fp-flight__flight-row-spacer" aria-hidden />

              <div className="hr-fp-flight__flight-times-cluster">
                <div className="flight-times hr-fp-flight__flight-times" dir="ltr">
                  <div className="flight-departure hr-fp-flight__flight-departure" translate="no">
                    {depShown}
                  </div>
                  <div className="flight-separator hr-fp-flight__flight-separator" aria-hidden>
                    →
                  </div>
                  <div className="flight-arrival hr-fp-flight__flight-arrival" translate="no">
                    {arrShown}
                  </div>
                </div>
                {leg.depCode && leg.arrCode ? (
                  <div className="hr-fp-flight__route-codes" translate="no">
                    {leg.depCode} → {leg.arrCode}
                  </div>
                ) : null}
                {leg.terminalDep || leg.terminalArr ? (
                  <div className="hr-fp-flight__terminals-hint" translate="no">
                    {[
                      leg.terminalDep ? `מראה: טרמינל ${leg.terminalDep}` : null,
                      leg.terminalArr ? `נחיתה: טרמינל ${leg.terminalArr}` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                ) : null}
                {(legTimelines || []).length > 1 && leg.duration && leg.duration !== "—" ? (
                  <div className="hr-fp-flight__leg-duration-hint" translate="no">
                    {leg.duration} בשמיים
                  </div>
                ) : null}
              </div>

              <div className="flight-airline hr-fp-flight__flight-airline">
                <SegmentAirlineLogo
                  key={`${leg.key}-logo`}
                  adminLogoUrl={adminForLeg}
                  airlineCode={legCode}
                />
                <div className="hr-fp-flight__flight-airline-line" translate="no">
                  {flightNumLine || "—"}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {metaStripParts.length > 0 ? (
        <p className="hr-fp-flight__meta-strip" translate="no">
          {metaStripParts.join(" · ")}
        </p>
      ) : null}
    </div>
  );
}
